// SearchableFileSelect - Reusable searchable file dropdown
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Search, X, Check, FileText, Box, Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils';

interface FileOption {
  fileName: string;
  s3Key?: string;
  isMultipart?: boolean;
  fileSize?: number;
}

interface SearchableFileSelectProps {
  files: FileOption[];
  selectedFile: string;
  onFileSelect: (fileKey: string) => void;
  isLoading?: boolean;
  placeholder?: string;
  className?: string;
}

// Helper to clean file names for display - hide internal implementation details
const getDisplayName = (fileName: string): string => {
  let name = fileName;
  // Remove common extensions
  name = name.replace(/\.(parquet|csv|json|xlsx|xls|tsv)$/i, '');
  // Remove double extensions like .csv.parquet
  name = name.replace(/\.(csv|json)\.(parquet)$/i, '');
  // Remove timestamp prefix (e.g., "1765940893209-")
  if (/^\d+-/.test(name)) {
    name = name.replace(/^\d+-/, '');
  }
  // Remove date-time suffix (e.g., "_20231215_143022")
  name = name.replace(/_\d{8}_\d{6}$/, '');
  // Remove "(X files)" or "(X parts)" suffixes
  name = name.replace(/\s*\(\d+\s+(files?|parts?)\)$/i, '');
  // Remove "_part_X" or "_chunk_X" suffixes
  name = name.replace(/[_-](part|chunk)[_-]?\d+$/i, '');
  // Remove trailing underscores or hyphens
  name = name.replace(/[_-]+$/, '');
  return name;
};

const formatFileSize = (bytes?: number): string => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export function SearchableFileSelect({
  files,
  selectedFile,
  onFileSelect,
  isLoading = false,
  placeholder = 'Select a file...',
  className,
}: SearchableFileSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });

  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Filter files based on search term
  const filteredFiles = useMemo(() => {
    if (!searchTerm.trim()) return files;
    const term = searchTerm.toLowerCase();
    return files.filter(f =>
      f.fileName.toLowerCase().includes(term) ||
      getDisplayName(f.fileName).toLowerCase().includes(term)
    );
  }, [files, searchTerm]);

  // Reset highlight when filtered list changes
  useEffect(() => {
    setHighlightedIndex(0);
  }, [filteredFiles.length]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 0);
    }
  }, [isOpen]);

  // Update dropdown position
  const openDropdown = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    }
    setIsOpen(true);
  };

  // Scroll highlighted item into view
  useEffect(() => {
    if (listRef.current && highlightedIndex >= 0) {
      const items = listRef.current.children;
      if (items[highlightedIndex]) {
        items[highlightedIndex].scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex]);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(i => Math.min(i + 1, filteredFiles.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(i => Math.max(i - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (filteredFiles[highlightedIndex]) {
          const file = filteredFiles[highlightedIndex];
          onFileSelect(file.s3Key || file.fileName);
          setIsOpen(false);
          setSearchTerm('');
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setSearchTerm('');
        break;
    }
  };

  // Get selected file display name
  const selectedFileName = useMemo(() => {
    const file = files.find(f => (f.s3Key || f.fileName) === selectedFile);
    return file ? getDisplayName(file.fileName) : '';
  }, [files, selectedFile]);

  const selectedFileInfo = useMemo(() => {
    return files.find(f => (f.s3Key || f.fileName) === selectedFile);
  }, [files, selectedFile]);

  return (
    <>
      {/* Trigger Button */}
      <button
        ref={triggerRef}
        type="button"
        onClick={() => {
          if (!isLoading) {
            if (isOpen) {
              setIsOpen(false);
            } else {
              openDropdown();
            }
          }
        }}
        disabled={isLoading}
        className={cn(
          "w-full flex items-center justify-between gap-2 px-3 py-2.5 text-sm",
          "bg-white dark:bg-gray-900 border rounded-lg",
          "transition-all duration-200",
          isOpen
            ? "border-blue-500 ring-2 ring-blue-500/20"
            : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600",
          isLoading && "opacity-60 cursor-not-allowed",
          className
        )}
      >
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {selectedFileInfo?.isMultipart ? (
            <Box className="h-4 w-4 text-blue-500 flex-shrink-0" />
          ) : selectedFile ? (
            <FileText className="h-4 w-4 text-gray-500 flex-shrink-0" />
          ) : (
            <Search className="h-4 w-4 text-gray-400 flex-shrink-0" />
          )}
          <span className={cn(
            "truncate",
            selectedFile ? "text-gray-900 dark:text-gray-100" : "text-gray-500"
          )}>
            {isLoading ? 'Loading files...' : (selectedFileName || placeholder)}
          </span>
        </div>
        <ChevronDown
          className={cn(
            "h-4 w-4 text-gray-400 flex-shrink-0 transition-transform",
            isOpen && "rotate-180"
          )}
        />
      </button>

      {/* Dropdown Panel */}
      {isOpen && createPortal(
        <div
          style={{
            position: 'fixed',
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            width: dropdownPosition.width,
            zIndex: 10000,
          }}
          className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl overflow-hidden"
          onKeyDown={handleKeyDown}
        >
          {/* Search Input */}
          <div className="p-2 border-b border-gray-200 dark:border-gray-700">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Type to search files..."
                className="w-full pl-9 pr-8 py-2 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded"
                >
                  <X className="h-3 w-3 text-gray-400" />
                </button>
              )}
            </div>
          </div>

          {/* File List */}
          <div
            ref={listRef}
            className="max-h-[280px] overflow-y-auto"
          >
            {isLoading ? (
              <div className="p-6 text-center text-gray-500 flex items-center justify-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading files...
              </div>
            ) : filteredFiles.length === 0 ? (
              <div className="p-6 text-center text-gray-500 text-sm">
                {files.length === 0 ? 'No files available' : 'No files match your search'}
              </div>
            ) : (
              filteredFiles.map((file, idx) => {
                const fileKey = file.s3Key || file.fileName;
                const displayName = getDisplayName(file.fileName);
                const isSelected = fileKey === selectedFile;
                const isHighlighted = idx === highlightedIndex;

                return (
                  <button
                    key={fileKey}
                    type="button"
                    onClick={() => {
                      onFileSelect(fileKey);
                      setIsOpen(false);
                      setSearchTerm('');
                    }}
                    onMouseEnter={() => setHighlightedIndex(idx)}
                    className={cn(
                      "w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors",
                      isHighlighted && "bg-gray-100 dark:bg-gray-800",
                      isSelected && "bg-blue-50 dark:bg-blue-900/30"
                    )}
                  >
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      {isSelected ? (
                        <Check className="h-4 w-4 text-blue-600" strokeWidth={3} />
                      ) : file.isMultipart ? (
                        <Box className="h-4 w-4 text-blue-500" />
                      ) : (
                        <FileText className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className={cn(
                        "text-sm truncate",
                        isSelected ? "font-medium text-blue-700 dark:text-blue-300" : "text-gray-900 dark:text-gray-100"
                      )}>
                        {displayName}
                      </div>
                      {file.fileSize && (
                        <div className="text-xs text-gray-500 truncate">
                          {formatFileSize(file.fileSize)}
                        </div>
                      )}
                    </div>
                  </button>
                );
              })
            )}
          </div>

          {/* Footer */}
          <div className="px-3 py-2 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
            <div className="flex items-center justify-between text-xs text-gray-500">
              <span>{filteredFiles.length} of {files.length} files</span>
              <span className="hidden sm:inline">↑↓ navigate • Enter select • Esc close</span>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Click outside handler */}
      {isOpen && createPortal(
        <div
          className="fixed inset-0 z-[9999]"
          onClick={() => {
            setIsOpen(false);
            setSearchTerm('');
          }}
        />,
        document.body
      )}
    </>
  );
}
